import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Copy, RefreshCw, Download, Trash2 } from 'lucide-react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import toast from 'react-hot-toast';
import { saveAs } from 'file-saver';
import { v4 as uuidv4, v1 as uuidv1 } from 'uuid';

interface GeneratedUuid {
  id: string;
  uuid: string;
  version: string;
  timestamp: Date;
}

export const UuidGeneratorTool: React.FC = () => {
  const { t } = useTranslation();
  const [uuids, setUuids] = useState<GeneratedUuid[]>([]);
  const [bulkCount, setBulkCount] = useState(10);
  const [selectedVersion, setSelectedVersion] = useState<'v4' | 'v1'>('v4');

  const generateSingleUuid = (version: 'v4' | 'v1' = selectedVersion) => {
    const uuid = version === 'v4' ? uuidv4() : uuidv1();
    const newUuid: GeneratedUuid = {
      id: uuidv4(),
      uuid,
      version,
      timestamp: new Date(),
    };
    
    setUuids(prev => [newUuid, ...prev]);
    return uuid;
  };

  const generateBulkUuids = () => {
    const count = Math.min(Math.max(1, bulkCount), 1000); // Limit between 1-1000
    const newUuids: GeneratedUuid[] = [];
    
    for (let i = 0; i < count; i++) {
      const uuid = selectedVersion === 'v4' ? uuidv4() : uuidv1();
      newUuids.push({
        id: uuidv4(),
        uuid,
        version: selectedVersion,
        timestamp: new Date(),
      });
    }
    
    setUuids(prev => [...newUuids, ...prev]);
    toast.success(t('tools.uuidGenerator.generatedCount', { count }));
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(t('common.copied'));
    } catch (err) {
      toast.error(t('common.failedToCopy'));
    }
  };

  const copyAllUuids = async () => {
    const allUuids = uuids.map(u => u.uuid).join('\n');
    await copyToClipboard(allUuids);
  };

  const downloadUuids = () => {
    if (uuids.length === 0) return;
    
    const content = uuids.map(u => 
      `${u.uuid} (${u.version.toUpperCase()}) - ${u.timestamp.toISOString()}`
    ).join('\n');
    
    const blob = new Blob([content], { type: 'text/plain' });
    saveAs(blob, `uuids-${new Date().toISOString().split('T')[0]}.txt`);
  };

  const clearAll = () => {
    setUuids([]);
    toast.success(t('tools.uuidGenerator.clearedAll'));
  };

  const removeUuid = (id: string) => {
    setUuids(prev => prev.filter(u => u.id !== id));
  };

  const formatUuid = (uuid: string, format: 'default' | 'uppercase' | 'nohyphens' | 'braces') => {
    switch (format) {
      case 'uppercase':
        return uuid.toUpperCase();
      case 'nohyphens':
        return uuid.replace(/-/g, '');
      case 'braces':
        return `{${uuid}}`;
      default:
        return uuid;
    }
  };

  return (
    <div className="space-y-6">
      {/* Generator Controls */}
      <Card>
        <CardHeader>
          <CardTitle>{t('tools.uuidGenerator.name')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Version Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('tools.uuidGenerator.uuidVersion')}
              </label>
              <div className="flex gap-2">
                <Button
                  onClick={() => setSelectedVersion('v4')}
                  variant={selectedVersion === 'v4' ? 'primary' : 'secondary'}
                  size="sm"
                >
                  {t('tools.uuidGenerator.version4')} ({t('tools.uuidGenerator.random')})
                </Button>
                <Button
                  onClick={() => setSelectedVersion('v1')}
                  variant={selectedVersion === 'v1' ? 'primary' : 'secondary'}
                  size="sm"
                >
                  {t('tools.uuidGenerator.version1')} ({t('tools.uuidGenerator.timeBased')})
                </Button>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                {selectedVersion === 'v4'
                  ? t('tools.uuidGenerator.v4Description')
                  : t('tools.uuidGenerator.v1Description')
                }
              </p>
            </div>

            {/* Generation Controls */}
            <div className="flex flex-wrap gap-2">
              <Button
                onClick={() => generateSingleUuid()}
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                {t('tools.uuidGenerator.generateSingle')}
              </Button>
              
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  min="1"
                  max="1000"
                  value={bulkCount}
                  onChange={(e) => setBulkCount(parseInt(e.target.value) || 10)}
                  className="w-20 h-10"
                />
                <Button
                  onClick={generateBulkUuids}
                  variant="secondary"
                >
                  {t('tools.uuidGenerator.bulk')}
                </Button>
              </div>

              {uuids.length > 0 && (
                <>
                  <Button
                    onClick={copyAllUuids}
                    variant="outline"
                  >
                    <Copy className="w-4 h-4 mr-2" />
                    {t('tools.uuidGenerator.copyAll')}
                  </Button>
                  <Button
                    onClick={downloadUuids}
                    variant="outline"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    {t('common.download')}
                  </Button>
                  <Button
                    onClick={clearAll}
                    variant="outline"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    {t('tools.uuidGenerator.clearAll')}
                  </Button>
                </>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Generated UUIDs */}
      {uuids.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>{t('tools.uuidGenerator.generatedUuids', { count: uuids.length })}</span>
              <span className="text-sm font-normal text-gray-500">
                {t('tools.uuidGenerator.clickToCopy')}
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {uuids.map((uuidItem) => (
                <div
                  key={uuidItem.id}
                  className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => copyToClipboard(uuidItem.uuid)}
                        className="font-mono text-sm text-gray-900 dark:text-gray-100 hover:text-primary-600 dark:hover:text-primary-400 cursor-pointer truncate"
                      >
                        {uuidItem.uuid}
                      </button>
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400">
                        {uuidItem.version.toUpperCase()}
                      </span>
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {t('tools.uuidGenerator.generated')}: {uuidItem.timestamp.toLocaleString()}
                    </div>
                  </div>
                  <div className="flex items-center space-x-1 ml-4">
                    <Button
                      onClick={() => copyToClipboard(uuidItem.uuid)}
                      size="sm"
                      variant="ghost"
                    >
                      <Copy className="w-3 h-3" />
                    </Button>
                    <Button
                      onClick={() => removeUuid(uuidItem.id)}
                      size="sm"
                      variant="ghost"
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Format Examples */}
      {uuids.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>{t('tools.uuidGenerator.formatExamples')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {uuids.length > 0 && (
                <>
                  {[
                    { name: t('tools.uuidGenerator.default'), format: 'default' as const },
                    { name: t('tools.uuidGenerator.uppercase'), format: 'uppercase' as const },
                    { name: t('tools.uuidGenerator.noHyphens'), format: 'nohyphens' as const },
                    { name: t('tools.uuidGenerator.withBraces'), format: 'braces' as const },
                  ].map(({ name, format }) => (
                    <div key={format} className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300 w-24">
                        {name}:
                      </span>
                      <button
                        onClick={() => copyToClipboard(formatUuid(uuids[0].uuid, format))}
                        className="flex-1 text-left font-mono text-sm bg-gray-50 dark:bg-gray-800 p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                      >
                        {formatUuid(uuids[0].uuid, format)}
                      </button>
                      <Button
                        onClick={() => copyToClipboard(formatUuid(uuids[0].uuid, format))}
                        size="sm"
                        variant="ghost"
                        className="ml-2"
                      >
                        <Copy className="w-3 h-3" />
                      </Button>
                    </div>
                  ))}
                </>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* UUID Information */}
      <Card>
        <CardHeader>
          <CardTitle>About UUIDs</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4 text-sm text-gray-600 dark:text-gray-400">
            <div>
              <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">UUID Version 4 (Random)</h4>
              <p>
                UUID v4 uses random or pseudo-random numbers. The probability of generating duplicate UUIDs is negligible.
                Format: xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx (where x is any hexadecimal digit and y is one of 8, 9, A, or B)
              </p>
            </div>
            <div>
              <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">UUID Version 1 (Time-based)</h4>
              <p>
                UUID v1 uses the current timestamp and MAC address. This ensures uniqueness across different machines and time.
                Format: xxxxxxxx-xxxx-1xxx-yxxx-xxxxxxxxxxxx (where the first part contains timestamp information)
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
